import { useGLTF } from "@react-three/drei";
import { useFrame, type ThreeEvent } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { getTileHeight } from "../../shared/room-layout";
import {
  resolveFurnitureFootprintCellsForAsset,
  resolveFurnitureRenderAlignmentOffset,
} from "../../shared/furniture-footprint";
import {
  normalizePaletteSlotName,
  paletteSlotFromMaterialName,
} from "../../shared/furniture-palette";
import type { RoomAsset, RoomLayoutRuntime } from "../../shared/types";
import { FRAME_TILE_HEIGHT_UNIT } from "../../shared/frame-placement";
import { normalizeGlb } from "../lib/glb-normalize";

const SELECTION_COLOR = new THREE.Color(0xff922b);
type TeleportVisualState = "closed" | "open" | "effects";
const DOOR_OPEN_CLIP_NAME = "door_open";
const DOOR_CLOSE_CLIP_NAME = "door_close";
const TELEPORT_OPEN_CLIP_NAME = "teleport_open";
const TELEPORT_EFFECTS_CLIP_NAME = "teleport_effects";
const TELEPORT_CLOSE_CLIP_NAME = "teleport_close";

function nearestFootprintCell(asset: RoomAsset, x: number, z: number): {
  x: number;
  z: number;
} {
  const cells = resolveFurnitureFootprintCellsForAsset(asset);
  let best = cells[0] ?? [Math.round(asset.x), Math.round(asset.z)];
  let bestDistSq = Number.POSITIVE_INFINITY;
  for (const [cellX, cellZ] of cells) {
    const dx = x - cellX;
    const dz = z - cellZ;
    const distSq = dx * dx + dz * dz;
    if (distSq < bestDistSq) {
      best = [cellX, cellZ];
      bestDistSq = distSq;
    }
  }
  return { x: best[0], z: best[1] };
}

function facingFromPoint(
  pointX: number,
  pointZ: number,
  cellX: number,
  cellZ: number
): number | undefined {
  const dx = pointX - cellX;
  const dz = pointZ - cellZ;
  if (Math.abs(dx) <= 0.05 && Math.abs(dz) <= 0.05) {
    return undefined;
  }
  if (Math.abs(dx) > Math.abs(dz)) {
    return dx > 0 ? Math.PI / 2 : -Math.PI / 2;
  }
  return dz > 0 ? 0 : Math.PI;
}

interface Props {
  asset: RoomAsset;
  layout?: RoomLayoutRuntime;
  editMode?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  onInteract?: (target: { x: number; z: number; facing?: number }) => void;
  onHoverCellChange?: (cell: { x: number; z: number } | null) => void;
}

export function NormalizedGlb({
  asset,
  layout,
  editMode,
  selected,
  onSelect,
  onInteract,
  onHoverCellChange,
}: Props) {
  const gltf = useGLTF(asset.url) as {
    scene: THREE.Group;
    animations?: THREE.AnimationClip[];
  };

  const tintColor = useMemo(() => {
    const value = asset.tintColor?.trim();
    if (!value) {
      return null;
    }
    try {
      return new THREE.Color(value);
    } catch {
      return null;
    }
  }, [asset.tintColor]);
  const paletteColorMap = useMemo(() => {
    const map = new Map<string, THREE.Color>();
    const source = asset.paletteColors;
    if (!source || typeof source !== "object") {
      return map;
    }
    for (const [rawSlot, rawColor] of Object.entries(source)) {
      const slot = normalizePaletteSlotName(rawSlot);
      if (!slot || typeof rawColor !== "string") {
        continue;
      }
      try {
        map.set(slot, new THREE.Color(rawColor));
      } catch {
        // Ignore invalid palette colors.
      }
    }
    return map;
  }, [asset.paletteColors]);

  const { object, scale, offset } = useMemo(() => {
    const cloned = gltf.scene.clone(true);
    if (tintColor || paletteColorMap.size > 0) {
      cloned.traverse((child) => {
        if (!(child as THREE.Mesh).isMesh) {
          return;
        }
        const mesh = child as THREE.Mesh;
        const sourceMaterial = mesh.material;
        const applyMaterialColor = (source: THREE.Material): THREE.Material => {
          const clonedMaterial = source.clone();
          if (!("color" in clonedMaterial) || !(clonedMaterial.color instanceof THREE.Color)) {
            return clonedMaterial;
          }
          const slot = paletteSlotFromMaterialName(clonedMaterial.name);
          const paletteColor =
            slot != null ? paletteColorMap.get(slot) ?? null : null;
          if (paletteColor) {
            clonedMaterial.color.copy(paletteColor);
            return clonedMaterial;
          }
          if (tintColor) {
            clonedMaterial.color.lerp(tintColor, 0.72);
          }
          return clonedMaterial;
        };
        if (Array.isArray(sourceMaterial)) {
          mesh.material = sourceMaterial.map(applyMaterialColor);
          return;
        }
        mesh.material = applyMaterialColor(sourceMaterial);
      });
    }
    const { scale: s, offset: o } = normalizeGlb(cloned);
    return { object: cloned, scale: s, offset: o };
  }, [gltf.scene, paletteColorMap, tintColor]);

  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const lastDoorStateRef = useRef<"open" | "closed" | null>(null);
  const lastTeleportStateRef = useRef<TeleportVisualState | null>(null);
  const resolvedDoorState: "open" | "closed" =
    asset.interactionType === "door" && asset.doorState === "open"
      ? "open"
      : "closed";
  const resolvedTeleportState: TeleportVisualState =
    asset.interactionType === "teleport" &&
    (asset.teleportState === "open" || asset.teleportState === "effects")
      ? asset.teleportState
      : "closed";
  const openClipName =
    asset.interactionType === "door" ? DOOR_OPEN_CLIP_NAME : "";
  const closeClipName =
    asset.interactionType === "door" ? DOOR_CLOSE_CLIP_NAME : "";

  useEffect(() => {
    const animations = gltf.animations ?? [];
    if (animations.length === 0) {
      mixerRef.current = null;
      lastDoorStateRef.current = null;
      lastTeleportStateRef.current = null;
      return;
    }
    const mixer = new THREE.AnimationMixer(object);
    mixerRef.current = mixer;
    lastDoorStateRef.current = null;
    lastTeleportStateRef.current = null;
    return () => {
      mixer.stopAllAction();
      mixer.uncacheRoot(object);
      mixerRef.current = null;
      lastDoorStateRef.current = null;
      lastTeleportStateRef.current = null;
    };
  }, [gltf.animations, object]);

  useFrame((_, delta) => {
    mixerRef.current?.update(delta);
  });

  useEffect(() => {
    if (asset.interactionType !== "door") {
      lastDoorStateRef.current = null;
      return;
    }
    const previous = lastDoorStateRef.current;
    const next = resolvedDoorState;
    lastDoorStateRef.current = next;
    if (previous === next) {
      return;
    }
    if (previous === null && next === "closed") {
      return;
    }
    const clipName = next === "open" ? openClipName : closeClipName;
    if (!clipName) {
      return;
    }
    const mixer = mixerRef.current;
    if (!mixer) {
      return;
    }
    const clip =
      (gltf.animations ?? []).find((candidate) => candidate.name === clipName) ??
      null;
    if (!clip) {
      return;
    }
    mixer.stopAllAction();
    const action = mixer.clipAction(clip, object);
    action.reset();
    action.clampWhenFinished = true;
    action.setLoop(THREE.LoopOnce, 1);
    action.enabled = true;
    action.play();
  }, [
    asset.interactionType,
    resolvedDoorState,
    openClipName,
    closeClipName,
    gltf.animations,
    object,
  ]);

  useEffect(() => {
    if (asset.interactionType !== "teleport") {
      lastTeleportStateRef.current = null;
      return;
    }
    const previous = lastTeleportStateRef.current;
    const next = resolvedTeleportState;
    lastTeleportStateRef.current = next;
    if (previous === next) {
      return;
    }
    if (previous === null && next === "closed") {
      return;
    }
    const clipName =
      next === "open"
        ? TELEPORT_OPEN_CLIP_NAME
        : next === "effects"
        ? TELEPORT_EFFECTS_CLIP_NAME
        : TELEPORT_CLOSE_CLIP_NAME;
    if (!clipName) {
      return;
    }
    const mixer = mixerRef.current;
    if (!mixer) {
      return;
    }
    const clip =
      (gltf.animations ?? []).find((candidate) => candidate.name === clipName) ??
      null;
    if (!clip) {
      return;
    }
    mixer.stopAllAction();
    const action = mixer.clipAction(clip, object);
    action.reset();
    action.clampWhenFinished = true;
    action.setLoop(THREE.LoopOnce, 1);
    action.enabled = true;
    action.play();
  }, [
    asset.interactionType,
    resolvedTeleportState,
    gltf.animations,
    object,
  ]);

  const edgeLines = useMemo(() => {
    if (!selected) return [];
    const lines: THREE.LineSegments[] = [];
    object.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        const edgeGeo = new THREE.EdgesGeometry(mesh.geometry, 30);
        const line = new THREE.LineSegments(
          edgeGeo,
          new THREE.LineBasicMaterial({ color: SELECTION_COLOR, linewidth: 1 })
        );
        line.position.copy(mesh.position);
        line.rotation.copy(mesh.rotation);
        line.scale.copy(mesh.scale);
        mesh.parent?.updateMatrixWorld(true);
        if (mesh.parent && mesh.parent !== object) {
          line.applyMatrix4(mesh.parent.matrixWorld);
        }
        lines.push(line);
      }
    });
    return lines;
  }, [object, selected]);

  const rotY = ((asset.rotation ?? 0) * Math.PI) / 180;
  const floorOffsetY = layout
    ? getTileHeight(layout, asset.x, asset.z) * FRAME_TILE_HEIGHT_UNIT
    : 0;
  const renderAlignmentOffset = useMemo(
    () => resolveFurnitureRenderAlignmentOffset(asset),
    [asset.blockWidth, asset.blockDepth, asset.rotation]
  );

  const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
    if (e.nativeEvent.button !== 0) {
      return;
    }
    if (editMode) {
      if (!onSelect) return;
      e.stopPropagation();
      onSelect();
      return;
    }
    if (onInteract) {
      e.stopPropagation();
      const target = nearestFootprintCell(asset, e.point.x, e.point.z);
      const facing = facingFromPoint(
        e.point.x,
        e.point.z,
        target.x,
        target.z
      );
      onInteract({ x: target.x, z: target.z, facing });
    }
  };

  const handlePointerMove = (e: ThreeEvent<PointerEvent>) => {
    if (!onHoverCellChange) {
      return;
    }
    e.stopPropagation();
    onHoverCellChange(nearestFootprintCell(asset, e.point.x, e.point.z));
  };

  return (
    <group
      position={[
        asset.x + renderAlignmentOffset.x,
        floorOffsetY + asset.y,
        asset.z + renderAlignmentOffset.z,
      ]}
      rotation={[0, rotY, 0]}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerOver={
        (e) => {
          handlePointerMove(e);
          if (editMode) {
            document.body.style.cursor = "pointer";
          }
        }
      }
      onPointerOut={() => {
        onHoverCellChange?.(null);
        if (editMode) {
          document.body.style.cursor = "";
        }
      }}
    >
      <group position={[offset.x, offset.y, offset.z]}>
        <primitive object={object} scale={[scale, scale, scale]} />
        {selected && (
          <group scale={[scale, scale, scale]}>
            {edgeLines.map((line, i) => (
              <primitive key={i} object={line} />
            ))}
          </group>
        )}
      </group>
    </group>
  );
}
